import { useEffect, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import { Link } from 'react-router-dom'
import { OutboundLink } from 'react-ga'
import Spinner from '../components/Spinner'
import classNames from 'classnames'
import { isPatron } from '../util/helpers'
import queryString from 'query-string'
import styles from '../styles/Patreon.module.scss'
import useDarkMode from 'use-dark-mode'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'

const Patreon = () => {
  const darkMode = useDarkMode(false)
  const auth = useSelector((state) => state.firebase.auth)
  const profile = useSelector((state) => state.firebase.profile)
  const { search } = useLocation()
  const [alert, setAlert] = useState({ message: '' })
  const [isLinkingPatreon, setIsLinkingPatreon] = useState(false)
  const [loadedWithCode, setLoadedWithCode] = useState(false)

  const client_id = process.env.REACT_APP_PATREON_CLIENT_ID
  //const redirect_uri = 'https://localhost:3000/account'
  const redirect_uri = 'https://edhrec.com/account'
  const scope = 'identity identity%5Bemail%5D'
  const accessToken = auth?.stsTokenManager?.accessToken

  useEffect(async () => {
    if (accessToken) {
      const { code } = queryString.parse(search)
      if (code && !isLinkingPatreon) {
        setIsLinkingPatreon(true)
        setLoadedWithCode(true)
        setAlert({ message: '' })
        window.history.replaceState(null, null, window.location.pathname)
        //const url = `http://127.0.0.1:5001/edhrec-1563844611919/us-central1/app/link_patreon?code=${code}`
        const url = `https://us-central1-edhrec-1563844611919.cloudfunctions.net/app/link_patreon?code=${code}`
        try {
          const res = await fetch(url, {
            headers: { Authorization: `Bearer ${accessToken}` },
          })
          const data = await res.json()
          if (data.success) {
            if (data.patron_status === 'active_patron') {
              setAlert({
                message: `Connected`,
                variant: 'success',
              })
            } else {
              setAlert({
                message: (
                  <>
                    {'No active subscription, see '}
                    <OutboundLink
                      eventLabel='https://www.patreon.com/edhrec'
                      target='_blank'
                      to='https://www.patreon.com/edhrec'
                    >
                      patreon.com/edhrec
                    </OutboundLink>
                  </>
                ),
                variant: 'warning',
              })
            }
          } else if (data.error) {
            setAlert({ message: data.error, variant: 'danger' })
          } else {
            setAlert({
              message: (
                <span>
                  {'Account not connected, see '}
                  <OutboundLink
                    eventLabel='https://www.patreon.com/edhrec'
                    target='_blank'
                    to='https://www.patreon.com/edhrec'
                  >
                    patreon.com/edhrec
                  </OutboundLink>
                </span>
              ),
              variant: 'warning',
            })
          }
        } catch (error) {
          setAlert({ message: error.message, variant: 'danger' })
        }
        setIsLinkingPatreon(false)
      }
    }
  }, [accessToken, search])

  // Refresh on initial patreon activation
  useEffect(() => {
    if (!isLinkingPatreon && loadedWithCode && isPatron(profile)) {
      window.location.reload(false)
    }
  }, [isLinkingPatreon, loadedWithCode, profile.patreon?.patron_status])

  return (
    <div
      className={classNames(styles.containerInner, {
        [styles.dark]: darkMode.value,
      })}
    >
      <Form.Group>
        <h3 className={styles.header}>Patreon</h3>
      </Form.Group>
      <Form.Group>
        {isLinkingPatreon ? (
          <div>
            <Spinner />
          </div>
        ) : isPatron(profile) ? (
          <>
            <p>
              Subscribed as <b>{profile.patreon.full_name}</b> until{' '}
              <b>{new Date(profile.patreon.next_charge_date).toDateString()}</b>
              .
            </p>
            <p>
              <OutboundLink
                eventLabel='https://www.patreon.com/settings/apps'
                target='_blank'
                to='https://www.patreon.com/settings/apps'
              >
                To join EDHREC Discord, you may need to disconnect then
                reconnect your Discord account on Patreon
                (https://www.patreon.com/settings/apps).
              </OutboundLink>
            </p>
            <p>
              {'Contact us '}
              <OutboundLink
                eventLabel='https://discord.gg/YWtxZS7qjH'
                target='_blank'
                to='https://discord.gg/YWtxZS7qjH'
              >
                on Discord
              </OutboundLink>
              {' or '}
              <Link to='/contact-us'>via email</Link>
              {' if you need support.'}
            </p>
          </>
        ) : (
          <>
            <p>
              <OutboundLink
                className={styles.patreonText}
                eventLabel='https://www.patreon.com/edhrec'
                target='_blank'
                to='https://www.patreon.com/edhrec'
              >
                Click here to view EDHREC on Patreon
              </OutboundLink>
            </p>
            <p>Support us on Patreon to unlock:</p>
            <ul>
              <li>Ad-free website layout</li>
              <li>Exclusive Discord with EDHREC staff and patrons</li>
              <li>Extra website features and early access to new features</li>
            </ul>
            <p>&nbsp;</p>
          </>
        )}
        {!isLinkingPatreon && (
          <a
            alt='Patreon'
            href={`https://www.patreon.com/oauth2/authorize?response_type=code&client_id=${client_id}&redirect_uri=${redirect_uri}&scope=${scope}`}
          >
            <Button className={styles.patreon}>
              {isPatron(profile) ? 'Reconnect' : 'Connect'}
              {' with Patreon'}
            </Button>
          </a>
        )}
      </Form.Group>
      {!isLinkingPatreon && profile.patreon && (
        <Form.Group>
          <Button
            onClick={async () => {
              setAlert({ message: '' })
              const url =
                'https://us-central1-edhrec-1563844611919.cloudfunctions.net/app/unlink_patreon'
              const res = await fetch(url, {
                headers: { Authorization: `Bearer ${accessToken}` },
              })
              const data = await res.json()
              if (data.error) {
                setAlert({ message: data.error, variant: 'danger' })
              }
            }}
            size='sm'
            variant='danger'
          >
            Disconnect
          </Button>
        </Form.Group>
      )}
      {alert.message && (
        <Form.Group>
          <Alert variant={alert.variant}>{alert.message}</Alert>
        </Form.Group>
      )}
    </div>
  )
}

export default Patreon
